import ApiException from './ApiException';
import TimeoutError from './TimeoutError';
import { formatTimeStamp, hasValue } from './utils';

const handleVersionCacheBust = (versionFromApi: string) => {
	if (hasValue(versionFromApi)) {
		const storedApiVersionNumber = localStorage.getItem('apiVersionNumber');
		if (!hasValue(storedApiVersionNumber)) {
			localStorage.setItem('apiVersionNumber', versionFromApi);
		} else if (storedApiVersionNumber !== versionFromApi) {
			localStorage.setItem('apiVersionNumber', versionFromApi);
			window.location.href = `${window.location.href}${
				window.location.href.includes('?') ? '&' : '?'
			}cacheBust=${formatTimeStamp(new Date())}`;
		}
	}
};

export const statuses = {
	ok: 200,
	badRequest: 400,
	unauthorized: 401,
	conflict: 409,
	serverError: 500
};

/**
 * @description Defines the basic interface to the API
 */
export default abstract class ApiBase {
	constructor(apiRootUrl: string) {
		this.apiRootUrl = apiRootUrl;
	}

	apiRootUrl: string;

	// TODO we should consider accepting an object for the query string, and using the createQueryString function
	// inside here to reduce the code for each API method.
	public async ajax(
		method: string,
		path: string,
		data: unknown = null,
		signal: AbortSignal = null,
		useToken = true,
		timeoutInMilliseconds: number = null,
		customAuthorizationHeader: string = null,
		dataIsFormData = false
	) {
		const headers: Record<string, string> = {
			'content-type': 'application/json'
		};

		if (dataIsFormData) {
			// We must delete the content type so that fetch will set it correctly for the form data.
			delete headers['content-type'];
		}

		if (useToken) {
			const accessToken = await this.getAccessToken();
			headers.Authorization = `Bearer ${accessToken}`;
		} else if (customAuthorizationHeader) {
			headers.Authorization = customAuthorizationHeader;
		}

		const request: RequestInit = {
			headers,
			method,
			mode: 'cors'
		};

		if (signal) request.signal = signal;
		if (data !== null) request.body = dataIsFormData ? (data as FormData) : JSON.stringify(data);

		let timedOut = false;

		return new Promise<unknown>(
			(resolve: (value: unknown) => void, reject: (reason?: TimeoutError | ApiException) => void) => {
				const timeoutHandle =
					timeoutInMilliseconds == null
						? null
						: setTimeout(() => {
								timedOut = true;
								reject(new TimeoutError());
						  }, timeoutInMilliseconds);

				fetch(this.apiRootUrl + path, request)
					.then(async (response) => {
						if (timeoutHandle) clearTimeout(timeoutHandle);
						if (timedOut) return;

						const text = await response.text();
						const responseBody = response.headers.get('Content-Type')?.includes('application/json')
							? ApiBase.parseJson(text)
							: text;

						// check if we need to bust the FE cache
						const apiVersionNumber = response.headers.get('Version-Number');
						handleVersionCacheBust(apiVersionNumber);

						if (response.ok) {
							resolve(responseBody);
						} else {
							// TODO Change the rejection to something like below. This will require handling the new error format
							// everywhere, which will be easier when we centralise more of our error handling code
							// (i.e. asyncActionHandler).
							// reject(
							// 	new Error(
							// 		`API Exception, status: ${response.status}, statusText: ${response.statusText}, body: ${responseBody}`
							// 	)
							// );
							// eslint-disable-next-line prefer-promise-reject-errors
							reject({
								status: response.status,
								statusText: response.statusText,
								body: responseBody
							} as ApiException);
						}
					})
					.catch((e) => {
						if (timedOut) return;

						// This leaves the promise unresolved, which is not a memory leak issue.
						// https://stackoverflow.com/questions/20068467/does-never-resolved-promise-cause-memory-leak
						// This solution is also why we need a .then() instead of await.

						// We are seeing some unhandled rejections leaking through this handler. Best thought is the TODO above along with
						// package updates, but it is too infrequent to pin down to a clear code issue.
						if (e.name === 'AbortError') return;
						if (e.message === 'Failed to fetch') {
							reject(new Error('Can not connect to server. Please check your Internet connection.'));
						} else {
							reject(e);
						}
					});
			}
		);
	}

	abstract getAccessToken(): Promise<string>;

	private static parseJson(value: string): unknown {
		// Attribution: https://stackoverflow.com/a/37563868/137996
		const fullIso8601DateRegex = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i;
		return JSON.parse(value, (name, reviverValue) => {
			if (typeof reviverValue === 'string' && fullIso8601DateRegex.test(reviverValue)) {
				return new Date(reviverValue);
			}
			return reviverValue;
		});
	}
}
